import {Fragment} from '@tiptap/pm/model'

/**
 * Wraps a block node in a blockNode.
 */
function wrapBlockContentInContainer(blockContentNode: any, schema: any): any {
  return schema.nodes['blockNode']!.create(null, blockContentNode)
}

/**
 * The block content that fronts a blockNode wrapping an orphan blockChildren.
 * A list or blockquote uses an invisible Slot to hold its childrenType.
 * A plain Group falls back to an empty paragraph.
 */
function orphanGroupPlaceholder(blockGroupNode: any, schema: any): any {
  const listType = blockGroupNode?.attrs?.listType
  if (listType && listType !== 'Group' && schema.nodes['slot']) {
    return schema.nodes['slot'].create({childrenType: listType})
  }
  return schema.nodes['paragraph']!.create()
}

/**
 * Wraps a blockChildren node in a blockNode. A list/blockquote is fronted by an
 * invisible Slot; a plain Group by an empty paragraph. If the previous blockNode
 * is a plain block with no children of its own, the blockChildren is merged into
 * it. the caller should then replace that previous node. Otherwise a fresh container
 * is returned and the caller must push it, so an existing node is not overwritten.
 */
function wrapBlockGroupInContainer(blockGroupNode: any, schema: any, previousNode: any): {node: any; merged: boolean} {
  if (
    previousNode &&
    previousNode.type.name === 'blockNode' &&
    previousNode.childCount === 1 &&
    previousNode.firstChild?.type.spec?.group === 'block'
  ) {
    return {
      node: schema.nodes['blockNode']!.create(previousNode.attrs, [previousNode.firstChild, blockGroupNode]),
      merged: true,
    }
  }

  return {
    node: schema.nodes['blockNode']!.create(null, [orphanGroupPlaceholder(blockGroupNode, schema), blockGroupNode]),
    merged: false,
  }
}

/**
 * This function fixes the blockNode in case it has multiple block node children after paste.
 * This is invalid structure
 */
export function splitBlockContainerNode(node: any) {
  const blockContents: any[] = []
  let blockGroup: any = null

  // Traverse blockNode's children and retrieve all block nodes and the last blockChildren node
  node.forEach((child: any) => {
    if (child.type.spec.group === 'block') {
      blockContents.push(child)
    } else if (child.type.name === 'blockChildren') {
      blockGroup = child
    }
  })

  // If there is only one block node, return the blockNode
  if (blockContents.length <= 1) {
    return [node]
  }

  // If there are multiple block nodes, split the blockNode into multiple blockNodes and return them
  const containers: any[] = []
  blockContents.forEach((blockContent, index) => {
    const children = [blockContent]
    if (index === blockContents.length - 1 && blockGroup) {
      children.push(blockGroup)
    }

    const attrs = {...node.attrs}
    if (attrs.id) {
      attrs.id = undefined
    }

    containers.push(node.type.create(attrs, children))
  })

  return containers
}

export function normalizeFragment(fragment: Fragment, schema?: any): Fragment {
  const nodes: any[] = []

  fragment.forEach((node: any) => {
    if (node.type.name === 'blockNode') {
      const containers = splitBlockContainerNode(node)
      containers.forEach((container: any) => {
        nodes.push(normalizeBlockContainer(container, schema))
      })
      return
    }

    if (node.type.name === 'blockChildren') {
      let groupNode = node
      let groupContent = normalizeFragment(node.content, schema)

      // Unwrap when the group has a blockNode child with a single blockChildren child
      // This is invalid structure and happens when copy pasting a nested blockChildren without a parent.
      if (groupContent.childCount === 1) {
        const firstChild = groupContent.firstChild

        if (
          firstChild &&
          firstChild.type?.name === 'blockNode' &&
          firstChild.firstChild &&
          firstChild.firstChild.type?.name === 'paragraph' &&
          firstChild.firstChild.content.content.length == 0 &&
          firstChild.lastChild &&
          firstChild.lastChild.type?.name === 'blockChildren'
        ) {
          const innerGroup = firstChild.lastChild

          // Make the inner group the root group
          groupNode = innerGroup
          groupContent = normalizeFragment(innerGroup.content, schema)
        }
      }

      if (groupNode.attrs?.listType === 'Group') {
        groupContent.forEach((childNode: any) => {
          nodes.push(childNode)
        })
        return
      }

      const normalizedGroup = groupNode.type.create(groupNode.attrs, groupContent)

      if (schema) {
        const prevNode = nodes[nodes.length - 1]
        const {node: wrappedContainer, merged} = wrapBlockGroupInContainer(normalizedGroup, schema, prevNode)
        // Replace the previous node only when the group was actually merged into
        // it. Otherwise push, so a preceding block (e.g. another list) survives.
        if (merged) {
          nodes[nodes.length - 1] = wrappedContainer
        } else {
          nodes.push(wrappedContainer)
        }
      } else {
        nodes.push(normalizedGroup)
      }
      return
    }

    // Wrap block nodes in blockNodes
    if (node.type.spec?.group === 'block') {
      if (!schema) {
        nodes.push(node)
        return
      }
      nodes.push(wrapBlockContentInContainer(node, schema))
      return
    }

    if (node.content && node.content.size > 0) {
      nodes.push(node.copy(normalizeFragment(node.content, schema)))
      return
    }

    nodes.push(node)
  })

  return Fragment.from(nodes)
}

export function normalizeBlockContainer(node: any, schema?: any) {
  const children: any[] = []

  // Traverse blockNode's children
  node.forEach((child: any, index: number) => {
    // If the child is a blockChildren, normalize it and add it to the children array
    if (child.type?.name === 'blockChildren') {
      children.push(child.type.create(child.attrs, normalizeFragment(child.content, schema)))
    } else if (child.content && child.content.size > 0) {
      children.push(child.copy(normalizeFragment(child.content, schema)))
    } else {
      children.push(child)
    }
  })

  // A blockNode with only a blockChildren is invalid structure. Front it with a
  // Slot (for a list/blockquote) or an empty paragraph (for a plain Group).
  if (children.length === 1 && children[0].type.name === 'blockChildren' && schema) {
    children.unshift(orphanGroupPlaceholder(children[0], schema))
  }

  return node.type.create(node.attrs, children)
}
